import { FC } from "react";
import { TFunction } from "i18next";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader } from "../../ui/card";
import { Button } from "../../ui/button";
import { AlertCircle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Props for the ErrorCard component
 */
interface Props {
  /** Error message to display (optional, uses locale default if not provided) */
  error?: string;
  /** Translation function from i18next */
  t: TFunction;
  /** Callback when retry button is clicked */
  onRetry: () => void;
  /** Whether the retry button is disabled (e.g., during retry attempt) */
  isRetrying?: boolean;
}

/**
 * Displays an error message with retry functionality for the AI chat.
 * Shows when the AI service fails or is unavailable.
 *
 * Features:
 * - Prominent error icon and title
 * - User-friendly error description
 * - Retry button to resend the question
 * - Support message for persistent errors
 *
 * @component
 * @example
 * <ErrorCard
 *   error="Connection failed"
 *   t={t}
 *   onRetry={handleRetry}
 * />
 */
const ErrorCard: FC<Props> = ({
  error,
  t,
  onRetry,
  isRetrying = false
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full"
    >
      <Card className={cn(
        "border-destructive/50 bg-destructive/5 dark:bg-destructive/10",
        "shadow-sm"
      )}>
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-destructive/15 dark:bg-destructive/20">
              <AlertCircle className="h-4 w-4 text-destructive" />
            </div>
            <div className="flex-1 space-y-1">
              <h3 className="text-sm font-semibold text-destructive">
                {t("components:documentchat.streamedresponse.error.title")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {error || t("components:documentchat.streamedresponse.error.description")}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pb-4 pt-0">
          <Button
            onClick={onRetry}
            disabled={isRetrying}
            variant="outline"
            size="sm"
            className="w-full border-destructive/30 hover:bg-destructive/10 dark:hover:bg-destructive/20"
          >
            <RefreshCw className={cn(
              "mr-2 h-4 w-4",
              isRetrying && "animate-spin"
            )} />
            {t("components:documentchat.streamedresponse.error.retrybtn")}
          </Button>
          <p className="text-xs text-muted-foreground">
            {t("components:documentchat.streamedresponse.error.contactsupport")}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ErrorCard;
